import { useQuery } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { authClient, sessionCheckUnanswered } from "@/lib/auth-client";
import { SessionUnreachable } from "@/components/SessionUnreachable";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";

/**
 * `/settings` with no org in it.
 *
 * A link that cannot know which org its reader will sign in to points here.
 * Resolve the member's first org and hand the fragment and query to that
 * org's real settings page.
 */
export function SettingsRedirect() {
  const { data: session, isPending, error: sessionError, refetch: refetchSession } = authClient.useSession();
  const meQuery = useQuery({
    queryKey: qk.me(),
    queryFn: () => client.me(),
    enabled: !!session?.user,
    staleTime: 30_000,
  });

  if (isPending || (session?.user && meQuery.isPending)) {
    return (
      <div className="auth-shell">
        <div className="spinner" />
      </div>
    );
  }
  if (!session?.user && sessionCheckUnanswered(sessionError)) return <SessionUnreachable onRetry={() => { void refetchSession(); }} />;
  if (!session?.user) return <Navigate to="/login" />;

  const firstOrg = meQuery.data?.orgs[0];
  if (!firstOrg) return <Navigate to="/onboarding" />;
  // `hash` is passed through verbatim: TanStack strips nothing.
  const hash = window.location.hash.replace(/^#/, "") || "integrations";
  return <Navigate to={`/o/${firstOrg.id}/settings`} hash={hash} search={parseSearch()} replace />;
}

function parseSearch(): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(window.location.search));
}
